import { useRef, type ChangeEvent, type ReactNode } from "react";

export interface Capture {
  blob: Blob;
  kind: "image" | "video";
  contentType: string;
  previewUrl: string;
}

/**
 * Point d'entrée capture : délègue entièrement à l'appareil photo natif du
 * téléphone plutôt qu'à une caméra web maison (getUserMedia). Plus fiable
 * (pas d'écran noir / permission qui capote selon l'appareil) et meilleure
 * qualité (pipeline caméra complet du téléphone : HDR, stabilisation…).
 *
 * `capture="environment"` déclenche directement l'appli caméra du téléphone
 * (photo ou vidéo selon `accept`) ; sans cet attribut, le champ ouvre le
 * sélecteur habituel (photothèque / fichiers).
 */
export function CameraCapture({ onCapture }: { onCapture: (c: Capture) => void }) {
  const photoRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const libraryRef = useRef<HTMLInputElement>(null);

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = ""; // permet de reprendre la même photo/vidéo une 2e fois
    if (!f) return;
    const kind = f.type.startsWith("video/") ? "video" : "image";
    onCapture({
      blob: f,
      kind,
      contentType: f.type || (kind === "video" ? "video/mp4" : "image/jpeg"),
      previewUrl: URL.createObjectURL(f),
    });
  }

  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8"
      style={{ background: "var(--theme-bg)" }}
    >
      <Option
        icon={<circle cx="12" cy="12" r="3.2" />}
        iconExtra={<path d="M4 8a2 2 0 0 1 2-2h1.5l1-1.5h5l1 1.5H18a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z" />}
        label="Prendre une photo"
        onClick={() => photoRef.current?.click()}
      />
      <Option
        icon={<path d="M4 7a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z" />}
        iconExtra={<path d="m16 10 4.5-2.5v9L16 14" strokeLinejoin="round" />}
        label="Filmer une vidéo"
        onClick={() => videoRef.current?.click()}
      />
      <Option
        icon={<rect x="3" y="4" width="18" height="16" rx="2" />}
        iconExtra={<path d="m3 16 5-4.5 4.5 3.5 3.5-3.5L21 15" strokeLinejoin="round" />}
        label="Choisir dans la photothèque"
        onClick={() => libraryRef.current?.click()}
      />

      <input
        ref={photoRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={onFile}
      />
      <input
        ref={videoRef}
        type="file"
        accept="video/*"
        capture="environment"
        hidden
        onChange={onFile}
      />
      <input
        ref={libraryRef}
        type="file"
        accept="image/*,video/*"
        hidden
        onChange={onFile}
      />
    </div>
  );
}

function Option({
  icon,
  iconExtra,
  label,
  onClick,
}: {
  icon: ReactNode;
  iconExtra: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex w-full max-w-xs items-center gap-4 rounded-2xl border px-5 py-4 text-left transition active:scale-[0.98]"
      style={{
        background: "var(--theme-surface)",
        borderColor: "var(--theme-border)",
      }}
    >
      <span
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
        style={{ background: "var(--theme-accent-soft)" }}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--theme-primary)"
          strokeWidth="1.7"
          strokeLinecap="round"
        >
          {icon}
          {iconExtra}
        </svg>
      </span>
      <span className="font-medium" style={{ color: "var(--theme-text)" }}>
        {label}
      </span>
    </button>
  );
}
